import { useMemo } from 'react'

import { usePrincipal } from '../../../../application/authentication'
import { isInputConfig } from '../../../../application/formBuilder/config'
import { usePolicy } from '../../../../application/product/policy/PolicyProvider'
import { Data, PolicyAdjustmentResponse } from '../../../../domain'
import { useAdjustments } from '../../../../state'
import { FormConfig } from '../../../../state/configuration'
import { useApiErrorHandlingAsyncCallback } from '../../../auth'
import { hasChanges } from '../helpers'
import { usePolicyAdjustmentRequest } from './adjustments'
import { toPolicyData } from './data'

export const usePolicyFormData = (form: FormConfig, hidden: FormConfig = []) => {
  const { policy } = usePolicy()
  const { getAdjustment } = useAdjustments()

  return useMemo(() => {
    const inputs = form.filter(isInputConfig)

    const toData = toPolicyData(policy.currentPolicyVersion)

    return [...inputs, ...hidden].reduce((data, input) => {
      const adjustment = getAdjustment(input.name)
      return {
        ...data,
        [input.name]: toData(adjustment),
      }
    }, {})
  }, [form, hidden, policy])
}

interface PolicyAdjustmentProps {
  form: FormConfig
  onSubmitted?: (response?: PolicyAdjustmentResponse) => void
}

export const usePolicyAdjustment = ({ form, onSubmitted }: PolicyAdjustmentProps) => {
  const { apiClient } = usePrincipal()

  const existingData = usePolicyFormData(form)
  const policyAdjustmentRequest = usePolicyAdjustmentRequest(form)

  return useApiErrorHandlingAsyncCallback(async (data: Data, effectiveAt?: string) => {
    const policyAdjustment = policyAdjustmentRequest(existingData, data, effectiveAt)

    if (hasChanges(policyAdjustment)) {
      const response = await apiClient.createPolicyAdjustment(policyAdjustment)
      onSubmitted?.(response)
    } else {
      onSubmitted?.()
    }
  })
}
